import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import MDLoader from "components/MDLoader";

function MerchantDeviceSelection({ merchant, selectedDevices, onDeviceSelectionChange, editMode }) {
  const api = useApi();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = () => {
    setLoading(true);
    api
      .get("devices/m/unmapped?id="+merchant?.id)
      .then((res) => {
        const deviceData = res.data?.data || [];
        setDevices(deviceData);
      })
      .catch((error) => {
        toast.error("Failed to fetch devices");
        console.error(error);
        setDevices([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeviceSelect = (event, value) => {
    if (!editMode) return;
    if (value && !selectedDevices.includes(value.slNo)) {
      onDeviceSelectionChange([...selectedDevices, value.slNo]);
    }
  };

  const handleRemoveDevice = (deviceId) => {
    if (!editMode) return;
    onDeviceSelectionChange(selectedDevices.filter((id) => id !== deviceId));
  };

  const selectedDeviceObjects = devices.filter((device) =>
    selectedDevices.includes(device.slNo)
  );

  const availableDevices = devices.filter(
    (device) => !selectedDevices.includes(device.slNo) && 
    (!device.merchantId || device.merchantId === null || device.merchantId === merchant?.id)
  );

  if (loading) {
    return <MDLoader />;
  }

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          Select Devices
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={3}>
          Search and select devices to associate with this merchant
        </MDTypography>

        {editMode && (
          <Autocomplete
            options={availableDevices}
            getOptionLabel={(option) =>
              `${option.serialNumber} - ${option.terminalName} (${option.terminalBrand})`
            }
            onChange={handleDeviceSelect}
            value={null}
            disabled={!editMode}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search and Select Device"
                placeholder="Type to search by serial number, name, or brand..."
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.slNo}>
                <MDBox width="100%">
                  <MDTypography variant="body2" fontWeight="medium">
                    {option.serialNumber} - {option.terminalName}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Brand: {option.terminalBrand} | Status: {option.deviceStatus || "N/A"} |
                    Group: {option.groupConfig?.grpConfigName || "N/A"}
                  </MDTypography>
                </MDBox>
              </li>
            )}
            sx={{ mb: 3 }}
          />
        )}

        <MDTypography variant="h6" fontWeight="medium" mb={2}>
          Selected Devices ({selectedDeviceObjects.length})
        </MDTypography>

        {selectedDeviceObjects.length === 0 ? (
          <MDBox
            sx={{
              border: "1px dashed #e0e0e0",
              borderRadius: "8px",
              p: 3,
              textAlign: "center",
            }}
          >
            <MDTypography variant="body2" color="text">
              No devices selected yet
            </MDTypography>
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            {selectedDeviceObjects.map((device) => (
              <Grid item xs={12} sm={6} md={4} key={device.slNo}>
                <Card
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    position: "relative",
                    height: "100%",
                  }}
                >
                  {editMode && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveDevice(device.slNo)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "error.main",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <MDBox pr={editMode ? 4 : 0}>
                    <MDTypography variant="body2" fontWeight="bold" mb={1}>
                      {device.terminalName}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      <strong>Serial:</strong> {device.serialNumber}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      <strong>Brand:</strong> {device.terminalBrand}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      <strong>Device ID:</strong> {device.slNo}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      <strong>Status:</strong> {device.deviceStatus || "N/A"}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      <strong>Group:</strong> {device.groupConfig?.grpConfigName || "N/A"}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>
    </Card>
  );
}

MerchantDeviceSelection.propTypes = {
  merchant: PropTypes.object,
  selectedDevices: PropTypes.array.isRequired,
  onDeviceSelectionChange: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
};

export default MerchantDeviceSelection;
